'use client';

import type { FormattedMessageProps } from './FormattedMessage.types';
import { useContext, useState, type FC } from 'react';
import styles from './FormattedMessage.module.css';
import { Text, Button, Flex, Icon, ButtonGroup } from '@/_components';
import { FormattedQuestionnaires } from './_components/FormattedQuestionnaires';
import { AdminAIContext } from '@/_contexts/admin/AdminAIProvider/AdminAIProvider';
import { renderMarkdown } from '@/_utils/markdown';
import { extractQuestionnaireData } from '@/_utils/ai';
import { useParams } from 'next/navigation';
import { getClasses } from '@/_utils/classnames';

export const FormattedMessage: FC<FormattedMessageProps> = ({
  content,
  questionnaireIds,
  questionIds,
  questionnairePairs,
  questionnaireMetadata,
  graphql_data,
}) => {
  const { openPreviewQuestionnaire, loadingQuestionnaireId } =
    useContext(AdminAIContext);

  const [areSourcesExpanded, setAreSourcesExpanded] = useState(false);
  const [areQuestionnairesExpanded, setAreQuestionnairesExpanded] =
    useState(false);
  const [isLegacySourceExpanded, setIsLegacySourceExpanded] = useState(false);

  const { questionnaireId: questionnaireIdParam } = useParams();

  if (!content) {
    return <div className={styles.formattedMessage}>No content to display</div>;
  }

  const containsQuestionnaire: boolean =
    content.includes('Questionnaire:') || content.includes('questionnaire:');

  const formatContent = () => {
    // Always strip any streaming cursor span before rendering
    const cleanContent = (content || '').replace(
      /<span class="[^"]*streamingCursor[^"]*">_<\/span>/g,
      '',
    );

    const markdownHtml = renderMarkdown(cleanContent);

    return <div dangerouslySetInnerHTML={{ __html: markdownHtml }} />;
  };

  // Extract questionnaire and question data from graphql_data if available
  const {
    questionnaireIds: extractedQuestionnaireIds,
    questionIds: extractedQuestionIds,
    questionnairePairs: extractedQuestionnairePairs,
  } = extractQuestionnaireData(graphql_data || []);

  // Use extracted data if available, otherwise fall back to existing data
  const finalQuestionnaireIds =
    extractedQuestionnaireIds.length > 0
      ? extractedQuestionnaireIds
      : questionnaireIds;
  const finalQuestionIds =
    extractedQuestionIds.length > 0 ? extractedQuestionIds : questionIds;
  const finalQuestionnairePairs =
    extractedQuestionnairePairs.length > 0
      ? extractedQuestionnairePairs
      : questionnairePairs;

  // Helper function to render sources container
  const renderSourcesContainer = (
    isExpanded: boolean,
    toggleExpanded: () => void,
    count: number,
    children: React.ReactNode,
  ) => (
    <Flex isColumn={true} paddingX={6} className={styles.sourcesContainer}>
      <ButtonGroup>
        <Button
          intent="ghost"
          iconLeft={isExpanded ? 'caret-down' : 'caret-right'}
          value={`View sources (${count})`}
          onClick={toggleExpanded}
          className={styles.sourcesToggleButton}
        />
      </ButtonGroup>
      {isExpanded && (
        <Flex isColumn gap={2}>
          {children}
        </Flex>
      )}
    </Flex>
  );

  // Helper function to get questionnaire display text
  const getQuestionnaireDisplayText = (
    questionnaireName?: string,
    companyName?: string,
    taxYear?: number,
  ) => {
    return `Questionnaire${questionnaireName ? `: ${questionnaireName}` : ''}${companyName ? ` — ${companyName}` : ''}${taxYear ? ` — ${taxYear}` : ''}`;
  };

  // If the content looks like a questionnaire list but we do NOT have structured ids, fallback to text formatter
  if (
    containsQuestionnaire &&
    (!finalQuestionnaireIds || finalQuestionnaireIds.length === 0)
  ) {
    return <FormattedQuestionnaires content={content} />;
  }

  const hasQuestionnaireLinks: boolean = Boolean(
    (!finalQuestionnairePairs || finalQuestionnairePairs.length === 0) &&
      finalQuestionnaireIds &&
      finalQuestionnaireIds.length > 0,
  );

  const hasLegacyQuestionnaireLinks: boolean = Boolean(
    !finalQuestionnairePairs &&
      finalQuestionnaireIds &&
      finalQuestionnaireIds.length > 0 &&
      finalQuestionIds &&
      finalQuestionIds.length > 0,
  );

  const formattedContent = formatContent();

  return (
    <Flex className={styles.formattedMessage} isColumn={true} gap={8}>
      <Text type="div" className={styles.formattedMessage__md}>
        {formattedContent}
      </Text>

      {/* Show questionnaire links if present */}
      {hasQuestionnaireLinks &&
        renderSourcesContainer(
          areQuestionnairesExpanded,
          () => setAreQuestionnairesExpanded(!areQuestionnairesExpanded),
          finalQuestionnaireIds?.length || 0,
          finalQuestionnaireIds?.map((questionnaireId, idx) => {
            const questionId =
              finalQuestionIds && finalQuestionIds[idx]
                ? finalQuestionIds[idx]
                : undefined;

            const metadata = questionnaireMetadata?.find(
              (meta) => String(meta.id) === String(questionnaireId),
            );

            const isActive: boolean =
              questionnaireIdParam === String(questionnaireId);

            const formattedMessageClasses: string = getClasses(
              styles.questionnaireItem,
              {
                [styles.questionnaireItemActive]: isActive,
              },
            );

            const formattedMessageIconClasses: string = getClasses(
              styles.formattedMessage__icon,
              {
                [styles.formattedMessage__iconActive]: isActive,
              },
            );

            const onOpenQuestionnaire = () =>
              questionnaireId &&
              openPreviewQuestionnaire(
                questionnaireId,
                typeof questionId === 'number' ? questionId : undefined,
              );

            return (
              <Flex
                padding={2}
                isYCentered={true}
                gap={4}
                key={questionnaireId || idx}
                onClick={onOpenQuestionnaire}
                className={formattedMessageClasses}
              >
                <Flex className={formattedMessageIconClasses} isCentered={true}>
                  <Icon size={16} name={isActive ? 'eye' : 'cursor-click'} />
                </Flex>
                <Text type="small" fontWeight="medium" color="currentcolor">
                  {getQuestionnaireDisplayText(
                    metadata?.name,
                    metadata?.company_name,
                    metadata?.tax_year,
                  )}
                </Text>
              </Flex>
            );
          }),
        )}

      {/* Add source link if questionnaire pairs exist */}
      {finalQuestionnairePairs &&
        finalQuestionnairePairs.length > 0 &&
        renderSourcesContainer(
          areSourcesExpanded,
          () => setAreSourcesExpanded(!areSourcesExpanded),
          finalQuestionnairePairs.length,
          finalQuestionnairePairs.map((pair, idx) => {
            // Get metadata from backend only - no fallback to context
            const metadata = questionnaireMetadata?.find(
              (meta) => String(meta.id) === String(pair.questionnaire_id),
            );

            return (
              <div
                key={`${pair.questionnaire_id}-${pair.question_id}-${idx}`}
                className={styles.sourceButtonWithText}
              >
                <a
                  href="#"
                  className={styles.sourceButton}
                  onClick={(e) => {
                    e.preventDefault();
                    openPreviewQuestionnaire(
                      pair.questionnaire_id,
                      pair.question_id,
                    );
                  }}
                >
                  {metadata && (
                    <>
                      {metadata.company_name || ''}
                      {metadata.name ? ` - ${metadata.name}` : ''}
                      {metadata.tax_year ? ` — ${metadata.tax_year}` : ''}
                    </>
                  )}
                  {(pair.question_text || pair.text) && (
                    <>
                      <br />
                      <span
                        className={styles.responseText}
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(
                            pair.question_text || pair.text || '',
                          ),
                        }}
                      />
                    </>
                  )}
                </a>
              </div>
            );
          }),
        )}

      {/* Legacy: Add source link if questionnaireIds exist but it's not a list format */}
      {hasLegacyQuestionnaireLinks &&
        renderSourcesContainer(
          isLegacySourceExpanded,
          () => setIsLegacySourceExpanded(!isLegacySourceExpanded),
          1,
          <Button
            intent="secondary"
            size="small"
            isLoading={finalQuestionnaireIds?.[0] === loadingQuestionnaireId}
            iconLeft="link"
            value="View Source"
            onClick={() =>
              openPreviewQuestionnaire(
                finalQuestionnaireIds?.[0]!,
                finalQuestionIds?.[0],
              )
            }
          />,
        )}
    </Flex>
  );
};
